/**
 * useViewAs — drives the Clerk actor-token impersonation flow (spec §7/§8).
 *
 *   start(targetUserId): backend mints a ticket -> signIn.create({strategy:'ticket'})
 *                        -> setActive(new session). The resulting session is an
 *                        impersonation session (JWT carries an `act` claim); the
 *                        backend enforces read-only.
 *   stop():              log the exit, then setActive back to the original
 *                        (admin) session.
 *
 * The original session id is stashed in localStorage so the exit can restore it
 * even after a full reload into the impersonated seat.
 */
import { useCallback, useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useClerk } from '../contexts/TestAuthContext';
import { startViewAs, logViewAsStop } from '../services/viewAsService';

const ORIG_SESSION_KEY = 'viewAs:originalSession';

/**
 * Restrict a view-as landing target to an internal, same-origin path. A caller
 * may hand us a route captured from a support ticket; this forecloses an open
 * redirect by accepting only paths that start with a single '/' (not '//', which
 * the browser treats as protocol-relative → another origin). Anything else —
 * an absolute URL, a `javascript:` string, a bare relative path, a non-string —
 * collapses to '/'.
 */
export function safeLandingPath(landingPath: unknown): string {
  return typeof landingPath === 'string' && /^\/(?!\/)/.test(landingPath) ? landingPath : '/';
}

export function useViewAs() {
  const { isLoaded, signIn, setActive } = useSignIn() as any;
  const clerk = useClerk() as any;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `landingPath` (default '/') lets a caller drop the impersonated session on a
  // specific page — e.g. the exact route a support ticket was filed from (Slice 1).
  // Guarded to internal absolute paths only: must start with a single '/' and not
  // '//' (protocol-relative) to foreclose an open redirect into another origin.
  const start = useCallback(async (targetUserId: string, landingPath: string = '/') => {
    setError(null);
    setBusy(true);
    try {
      const safeLanding = safeLandingPath(landingPath);
      const originalSessionId = clerk?.session?.id;
      if (originalSessionId) {
        try { localStorage.setItem(ORIG_SESSION_KEY, originalSessionId); } catch { /* storage blocked */ }
      }
      const { ticket } = await startViewAs(targetUserId);
      if (!isLoaded || !signIn || !setActive) throw new Error('Authentication is not ready yet.');
      const res = await signIn.create({ strategy: 'ticket', ticket });
      await setActive({ session: res.createdSessionId });
      // Hard navigation re-reads the new session everywhere (rail, workdesk, token).
      // Lands on the impersonated user's home by default, or the requested page.
      window.location.assign(safeLanding);
    } catch (e: any) {
      setError(e?.message || 'Could not start view-as.');
      setBusy(false);
      throw e;
    }
  }, [clerk, isLoaded, signIn, setActive]);

  const stop = useCallback(async () => {
    setBusy(true);
    await logViewAsStop();
    let original: string | null = null;
    try { original = localStorage.getItem(ORIG_SESSION_KEY); } catch { /* storage blocked */ }
    try {
      if (original && clerk?.setActive) {
        await clerk.setActive({ session: original });
      }
      try { localStorage.removeItem(ORIG_SESSION_KEY); } catch { /* storage blocked */ }
    } finally {
      window.location.assign('/');
    }
  }, [clerk]);

  return { start, stop, busy, error };
}
